import {Controller} from "@hotwired/stimulus"

// A file input you can drop onto.
//
// The sibling of `file-drop`, minus the upload: that one posts the file over XHR and
// shows a progress bar, this one just hands the file to the input and lets the form
// carry it. Reach for this wherever a plain `file_field` used to sit.
//
// The zone is a <label> wrapping an `ice:sr-only` input, so a click and Enter open the
// picker with no JS at all and the input stays focusable and submittable. Everything
// here is what a native input cannot do on its own: the drag highlight, the name and
// size of what was picked, checking the size before the form goes anywhere, and
// putting the file back.
//
// Markup contract — see shared/components/_ice_file_field.
export default class extends Controller {
    static targets = ["input", "zone", "card", "name", "meta", "error"]
    static values = {
        maxSize: {type: Number, default: 25 * 1024 * 1024}
    }

    connect() {
        this.depth = 0
    }

    dragEnter(event) {
        event.preventDefault()
        this.depth += 1
        this.zoneTarget.dataset.dragging = ""
    }

    dragOver(event) {
        event.preventDefault()
    }

    dragLeave(event) {
        event.preventDefault()
        this.depth = Math.max(0, this.depth - 1)
        if (this.depth === 0) this.clearHighlight()
    }

    drop(event) {
        event.preventDefault()
        this.clearHighlight()

        const files = event.dataTransfer.files
        if (!files || files.length === 0) return

        // The input is the one source of truth — the form submits what is in it, not
        // what was dropped on the label.
        const transfer = new DataTransfer()
        const wanted = this.inputTarget.multiple ? Array.from(files) : [files[0]]
        wanted.forEach((file) => transfer.items.add(file))
        this.inputTarget.files = transfer.files

        this.change()
    }

    change() {
        this.hideError()

        const files = Array.from(this.inputTarget.files || [])
        if (files.length === 0) return this.reset()

        const tooBig = files.find((file) => file.size > this.maxSizeValue)
        if (tooBig) {
            this.clear()
            return this.fail(`${tooBig.name} is ${this.humanSize(tooBig.size)}. The limit is ${this.humanSize(this.maxSizeValue)}.`)
        }

        const rejected = files.find((file) => !this.accepted(file))
        if (rejected) {
            this.clear()
            return this.fail(`${rejected.name} is not a file this accepts.`)
        }

        this.show(files)
    }

    remove(event) {
        // The button lives inside the label, so a click on it would otherwise reopen
        // the picker on the way back up.
        if (event) event.preventDefault()

        this.clear()
        this.reset()
        this.hideError()
    }

    // The same rules the browser applies to the accept attribute, since dropping a
    // file bypasses it — extensions, exact types, and wildcards like image/*.
    accepted(file) {
        const accept = this.inputTarget.accept
        if (!accept) return true

        const name = file.name.toLowerCase()
        const type = (file.type || "").toLowerCase()

        return accept.split(",").map((rule) => rule.trim().toLowerCase()).filter(Boolean).some((rule) => {
            if (rule.startsWith(".")) return name.endsWith(rule)
            if (rule.endsWith("/*")) return type.startsWith(rule.slice(0, -1))
            return type === rule
        })
    }

    show(files) {
        if (!this.hasCardTarget) return

        const names = files.map((file) => file.name)
        const size = files.reduce((total, file) => total + file.size, 0)

        this.nameTarget.textContent = names.length > 1 ? `${names.length} files` : names[0]
        if (this.hasMetaTarget) {
            this.metaTarget.textContent = names.length > 1
                ? `${names.join(", ")} — ${this.humanSize(size)}`
                : this.humanSize(size)
        }

        this.cardTarget.classList.remove("ice:hidden")
        this.zoneTarget.classList.add("ice:hidden")
    }

    reset() {
        if (!this.hasCardTarget) return

        this.cardTarget.classList.add("ice:hidden")
        this.zoneTarget.classList.remove("ice:hidden")
    }

    clear() {
        this.inputTarget.value = ""
    }

    fail(message) {
        if (!this.hasErrorTarget) return

        this.errorTarget.textContent = message
        this.errorTarget.classList.remove("ice:hidden")
    }

    hideError() {
        if (this.hasErrorTarget) this.errorTarget.classList.add("ice:hidden")
    }

    clearHighlight() {
        this.depth = 0
        delete this.zoneTarget.dataset.dragging
    }

    humanSize(bytes) {
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }
}
